import {
  Box,
  Button,
  FormControl,
  IconButton,
  ListItemText,
  MenuItem,
  MenuList,
  Stack,
} from '@chronosphereio/chrono-ui';
// This is a temporary override for an old use case until we complete the Chrono UI Popover backfill (sc-138310)
// eslint-disable-next-line no-restricted-imports
import { Popover } from '@mui/material';
import { Draft, produce } from 'immer';
import { DeleteOutline as DeleteIcon, Plus as PlusIcon } from 'mdi-material-ui';
import { RefObject, useRef, useState } from 'react';
import { ObjectEntries, Override, OverrideMatcherType } from '@/core';
import { OverrideRegexPreview } from './OverrideRegexPreview';
import { OverrideSeriesAutocomplete } from './OverrideSeriesAutocomplete';
import { BaseOverrideSchema, OverrideOptions } from './types';

type OverrideInputProps<T extends BaseOverrideSchema> = {
  override: Override<T>;
  onOverrideChange: (newOverride: Override<T>) => void;
  seriesNames: string[];
  onDelete: () => void;
  inputRef?: RefObject<HTMLInputElement>;
  overrideOptions: OverrideOptions<T>;
};

export function OverrideInput<T extends BaseOverrideSchema>({
  override,
  onOverrideChange,
  onDelete,
  inputRef,
  seriesNames,
  overrideOptions,
}: OverrideInputProps<T>) {
  const [isAddPropertyOpen, setIsAddPropertyOpen] = useState(false);
  const ref = useRef<HTMLButtonElement>(null);

  const handleValueChange = (newValue: string, matcherType?: OverrideMatcherType) => {
    onOverrideChange(
      produce(override, (draft) => {
        draft.value = newValue;
        if (matcherType) {
          draft.matcher = {
            type: matcherType,
          };
        }
      })
    );
  };

  function handlePropertyChange<Key extends keyof T>(property: Key, value: T[Key]) {
    onOverrideChange(
      produce(override, (draft) => {
        const key = property as keyof Draft<T>;
        const castValue = value as unknown as Draft<T>[keyof Draft<T>];
        draft.properties[key] = castValue;
      })
    );
  }

  const handleRemoveProperty = (property: string) => {
    onOverrideChange(
      produce(override, (draft) => {
        delete draft.properties[property];
      })
    );
  };

  const entries = Object.entries(overrideOptions) as ObjectEntries<OverrideOptions<T>>;
  const addableProperties = entries.filter(([_, option]) => !Object.hasOwn(override.properties, option.property));
  const usedProperties = entries.filter(([_, option]) => Object.hasOwn(override.properties, option.property));

  return (
    <div>
      <Stack direction="row" paddingY={1} spacing={1}>
        <Box flexGrow={1}>
          {/* Gives input as much room as possible since series names can be long */}
          <FormControl sx={{ width: '100%', marginRight: 1 }}>
            {override.matcher?.type === 'ByRegex' ? (
              <OverrideRegexPreview
                value={override.value}
                onChange={(newValue) => handleValueChange(newValue, override.matcher?.type)}
                seriesNames={seriesNames}
                inputRef={inputRef}
              />
            ) : (
              <OverrideSeriesAutocomplete
                value={override.value}
                onChange={(newValue) => handleValueChange(newValue, override.matcher?.type)}
                seriesNames={seriesNames}
                inputRef={inputRef}
              />
            )}
          </FormControl>
        </Box>

        <Box flexGrow={0}>
          <IconButton aria-label={`Delete override ${override.value}`} onClick={onDelete}>
            <DeleteIcon />
          </IconButton>
        </Box>
      </Stack>

      {usedProperties.map(([_, option]) => {
        return (
          <option.Component
            key={option.property}
            onChange={(nextValue) => handlePropertyChange(option.property, nextValue)}
            onRemove={() => handleRemoveProperty(option.property)}
            title={option.title}
            value={override.properties[option.property] as T[keyof T]}
          />
        );
      })}

      <Box pt={1} pb={1}>
        {addableProperties.length > 0 && (
          <>
            <Button
              aria-describedby="add-property"
              StartIcon={<PlusIcon />}
              variant="tertiary"
              size="small"
              ref={ref}
              disabled={addableProperties.length === 0}
              onClick={() => setIsAddPropertyOpen(true)}
            >
              Add property
            </Button>
            <Popover
              id={'add-property'}
              open={isAddPropertyOpen}
              anchorEl={ref.current}
              onClose={() => setIsAddPropertyOpen(false)}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
              }}
            >
              <div>
                <MenuList>
                  {addableProperties.map(([, option]) => {
                    return (
                      <MenuItem
                        key={option.property}
                        onClick={() => {
                          setIsAddPropertyOpen(false);
                          handlePropertyChange(option.property, option.default as T[string]);
                        }}
                      >
                        <ListItemText>{option.title}</ListItemText>
                      </MenuItem>
                    );
                  })}
                </MenuList>
              </div>
            </Popover>
          </>
        )}
      </Box>
    </div>
  );
}
